import { isNil, pipe, replace, toLower, toString } from 'rambda'
import i18n from '@/i18n'

// ------------------------- Primitives -----------------------
export const ellipsisMiddle = (string, headLength = 3, tailLength = 3) => {
  if (!string) return fallback(string)

  const lengthVisible = headLength + 3 + tailLength

  if (lengthVisible >= string.length) return string

  return string
    .slice(0, headLength) + '...' + string.slice(-tailLength)
}

export const capitalizeFirst = string => string.charAt(0).toUpperCase() + string.slice(1)

export const lower = string => string?.toLowerCase()

export const decimal = (() => {
  const everyThreeDigits = /\B(?=(\d{3})+(?!\d))/g

  return (number, precision = 2) => {
    if (isNil(number)) return null

    // add commas every 3 digits
    const parts = number.toFixed(precision).split('.')
    parts[0] = parts[0].replace(everyThreeDigits, ',')

    return parts.join('.')
  }
})()

export const round = (number, precision) => {
  if (isNil(number)) return null

  return Number(number.toFixed(precision))
}

export const percent = number => `${number}%`

export const jsonPretty = jsonString =>
  JSON.stringify(JSON.parse(jsonString), null, 2)

export const nth = (number) => {
  const suffix = (() => {
    const tenReminder = number % 10
    const hundredReminder = number % 100
    if (tenReminder === 1 && hundredReminder !== 11) return 'st'
    if (tenReminder === 2 && hundredReminder !== 12) return 'nd'
    if (tenReminder === 3 && hundredReminder !== 13) return 'rd'
    return 'th'
  })()

  return `${number}${suffix}`
}

export const currency = (number, currency = '$', precision = 2) =>
  `${currency || 0}${decimal(number, precision)}`

export const fallback = string => string || '-'

export const yesNo = boolean => i18n.t(boolean ? 'misc.yes' : 'misc.no')

export const initials = ({ firstName, lastName }) => [firstName, lastName]
  .filter(Boolean)
  .map(name => name.charAt(0))
  .join('')
  .toUpperCase()

export const name = ({ firstName, lastName }) => [firstName, lastName]
  .filter(Boolean)
  .join(' ')

export const slug = pipe(
  toString,
  toLower,
  replace(/\s+/g, '-'), // Replace spaces with -
  replace(/&/g, ''), // Remove &
  replace(/[^\w-]+/g, ''), // Remove all non-word characters
  replace(/--+/g, '-'), // Replace multiple - with single -
  replace(/^-+/, ''), // Trim - from start of text
  replace(/-+$/, ''), // Trim - from end of text
)

export const metricNumber = number => ['K', 'M', 'B']
  .reduce((accumulator, letter) => parseInt(accumulator, 10) > 1000
    ? Math.round(parseInt(accumulator, 10) / 1000) + letter
    : accumulator,
  number)

export const paragraphs = text => (text || '').replace(/\n+/g, '\n')

// ------------------------- Time -------------------------
export const isoDateTime = (date) => {
  if (!date) return fallback(date)

  return date.toISOString().split('.')[0]
}

export const { relativeDuration, relativeTime } = (() => {
  const { abs } = Math
  const SECOND = 1000
  const MINUTE = 60 * SECOND
  const HOUR = 60 * MINUTE
  const DAY = 24 * HOUR
  const MONTH = 30 * DAY
  const YEAR = 365 * DAY

  const times = [
    1,
    SECOND,
    MINUTE,
    HOUR,
    DAY,
    MONTH,
    YEAR,
    YEAR * YEAR,
  ]

  function relativeDuration(ms) {
    const msAbsolute = abs(ms)

    if (abs(msAbsolute) <= SECOND) return i18n.tc('time.just')

    const unitIndex = times.findIndex((_, index) => msAbsolute < times[index + 1])
    const unitMs = times[unitIndex]

    if (!unitMs) return null

    const amount = Math.round(msAbsolute / unitMs)

    const unitLabel = i18n.tc(`time.long.${unitIndex}`, amount)

    return `${amount} ${unitLabel}`
  }

  function relativeTime(inputDate) {
    const duration = Date.now() - inputDate.getTime()

    if (abs(duration) <= SECOND) return i18n.tc('time.just')

    const durationRelative = relativeDuration(duration)
    const translationKey = `time.${duration >= 0 ? 'ago' : 'in'}`

    return i18n.t(translationKey, [durationRelative])
  }

  return {
    relativeDuration,
    relativeTime,
  }
})()

export const relativeTimeDiff = (date1, date2) =>
  relativeDuration(date1.getTime() - date2.getTime())
